/**
 * Labstep
 *
 * @module state/reducers/thumbnail
 * @desc Reducers for thumbnail
 */

import type { Action } from 'labstep-web/models/action.model';
import { REFRESH_THUMBNAIL_STATUSES_ACTION_TYPE } from 'labstep-web/state/actions/thumbnail';
import omit from 'lodash/omit';
import pickBy from 'lodash/pickBy';
import { combineReducers } from 'redux';

// Complete interface for thumbnail reducer state
export interface StateThumbnail {
  thumbnails: IThumbnailCollection;
}

export interface IThumbnailCollection {
  [key: string]: IThumbnailItem;
}

export interface IThumbnailItem {
  fileGuid: string;
  params: Record<string, unknown>;
  url: string | null;
  status: ThumbnailStatus;
  retry: number;
}

export type ThumbnailStatus =
  | 'loading'
  | 'loaded'
  | 'failed'
  | 'error'
  | 'waiting_for_fresh_jwt'
  | null;

// Action interfaces for complete type safety
export interface IThumbnailActionMeta {
  origin?: any;
  key: string;
  fileGuid: string;
  params: Record<string, unknown>;
}

export interface IThumbnailActionPayload {
  url: string;
}

export interface IThumbnailActionError {
  data: {
    message?: string;
  };
}

export interface IThumbnailAction {
  type: string;
  meta: IThumbnailActionMeta;
  payload?: IThumbnailActionPayload;
  error?: IThumbnailActionError;
}

// Specific action type interfaces
export interface IServerlessRequestThumbnailAction
  extends IThumbnailAction {
  type: 'SERVERLESS_REQUEST_THUMBNAIL';
}

export interface IServerlessSuccessThumbnailAction
  extends IThumbnailAction {
  type: 'SERVERLESS_SUCCESS_THUMBNAIL';
  payload: IThumbnailActionPayload;
}

export interface IServerlessFailThumbnailAction
  extends IThumbnailAction {
  type: 'SERVERLESS_FAIL_THUMBNAIL';
  error?: IThumbnailActionError;
}

export interface IThumbnailErrorAction extends IThumbnailAction {
  type: 'THUMBNAIL_ERROR';
}

export interface IJwtExpiredAction extends IThumbnailAction {
  type: 'JWT_EXPIRED';
  meta: IThumbnailActionMeta & {
    origin: IServerlessRequestThumbnailAction;
  };
}

export interface IThumbnailFlushAction {
  type: 'THUMBNAIL_FLUSH_WITH_TIMESTAMP';
}

export interface IRefreshThumbnailStatusesAction {
  type: typeof REFRESH_THUMBNAIL_STATUSES_ACTION_TYPE;
}

// Union type for all possible thumbnail actions
export type ThumbnailActionTypes =
  | IServerlessRequestThumbnailAction
  | IServerlessSuccessThumbnailAction
  | IServerlessFailThumbnailAction
  | IThumbnailErrorAction
  | IJwtExpiredAction
  | IThumbnailFlushAction
  | IRefreshThumbnailStatusesAction;

const regex = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\+\d{2}:\d{2}/;

export const flushWithTimestamp = (
  state: IThumbnailCollection,
): IThumbnailCollection =>
  pickBy(state, (_, key) => !regex.test(key)) as IThumbnailCollection;

/**
 * List of thumbnails
 */
export const thumbnails = (
  state: IThumbnailCollection = {},
  action: Action,
) => {
  if (action.type === 'SERVERLESS_REQUEST_THUMBNAIL') {
    return {
      ...state,
      [action.meta.key]: {
        ...state[action.meta.key],
        fileGuid: action.meta.fileGuid,
        params: action.meta.params,
        status: 'loading',
        url: null,
      },
    };
  }
  if (action.type === 'SERVERLESS_SUCCESS_THUMBNAIL') {
    return {
      ...state,
      [action.meta.key]: {
        ...state[action.meta.key],
        status: 'loaded',
        url: action.payload.url,
      },
    };
  }
  if (action.type === 'SERVERLESS_FAIL_THUMBNAIL') {
    // "File is a link" results from a bug on the web
    // This has been fixed, but the cache may still have
    // these files stored, so we need to flush these out
    if (action.error?.data?.message === 'File is a link') {
      return omit(state, action.meta.key);
    }
    return {
      ...state,
      [action.meta.key]: {
        ...state[action.meta.key],
        status: 'failed',
        url: null,
      },
    };
  }
  if (action.type === 'THUMBNAIL_ERROR') {
    return {
      ...state,
      [action.meta.key]: {
        ...state[action.meta.key],
        fileGuid: action.meta.fileGuid,
        params: action.meta.params,
        status: 'error',
        retry: state[action.meta.key].retry
          ? state[action.meta.key].retry + 1
          : 1,
      },
    };
  }
  if (
    action.type === 'JWT_EXPIRED' &&
    action.meta &&
    action.meta.origin &&
    action.meta.origin.type === 'SERVERLESS_REQUEST_THUMBNAIL'
  ) {
    return {
      ...state,
      [action.meta.origin.meta.key]: {
        ...state[action.meta.origin.meta.key],
        status: 'waiting_for_fresh_jwt',
        url: null,
      },
    };
  }
  if (action.type === 'THUMBNAIL_FLUSH_WITH_TIMESTAMP') {
    return flushWithTimestamp(state);
  }

  // Fixes: https://linear.app/labstep/issue/LAB-1434/images-uploaded-to-experiment-not-showing-for-some-users
  if (action.type === REFRESH_THUMBNAIL_STATUSES_ACTION_TYPE) {
    return Object.keys(state).reduce(
      (result, key) => ({
        ...result,
        [key]: {
          ...state[key],
          status: state[key].status !== 'loaded' ? null : 'loaded',
        },
      }),
      {},
    );
  }

  return state;
};

const reducers = combineReducers({
  thumbnails,
});

export default reducers;
